import { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import EmergencyRequestAddressField from "./EmergencyRequestAddressField";
import "./EmergencyRequestForm.css";

/**
 * Emergency Booking Widget intake form — a SEPARATE system from the existing
 * instant-auto-match EmergencyCareModal (see
 * EMERGENCY_BOOKING_MODULE_CHECKLIST.md §1). Shared between the public
 * homepage surface (requireOtp) and the logged-in customer dashboard surface
 * (OTP skipped, name/mobile prefilled) rather than duplicating the form.
 */

const SERVICE_TYPES = [
  { value: "CARE_ATTENDANT", label: "Care Attendant" },
  { value: "NURSE", label: "Nurse" },
  { value: "PHYSIOTHERAPIST", label: "Physiotherapist" },
];

const REQUIRED_TIMES = [
  { value: "IMMEDIATELY", label: "Immediately" },
  { value: "WITHIN_2_HOURS", label: "Within 2 Hours" },
  { value: "TODAY", label: "Today" },
  { value: "TOMORROW", label: "Tomorrow" },
];

const SHIFT_TYPES = [
  { value: "HOURLY", label: "Hourly" },
  { value: "TWELVE_HOUR", label: "12 Hours" },
  { value: "TWENTY_FOUR_HOUR_LIVE_IN", label: "24 Hours Live-in" },
];

const DEFAULT_OTP_PURPOSE = "EMERGENCY_REQUEST_VERIFICATION";
const NOTES_MAX_LENGTH = 100;

function normalizeMobileDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

const SERVICE_LABELS = Object.fromEntries(SERVICE_TYPES.map((opt) => [opt.value, opt.label]));

function formatWhen(value) {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function EmergencyRequestForm({
  requireOtp: requireOtpProp,
  source = "HOMEPAGE",
  initialName = "",
  initialMobile = "",
  registeredMobile = "",
  onSuccess,
  onCancel,
}) {
  const [serviceType, setServiceType] = useState("");
  const [requiredTime, setRequiredTime] = useState("");
  const [shiftType, setShiftType] = useState("");
  const [address, setAddress] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [name, setName] = useState(initialName);
  const [mobile, setMobile] = useState(normalizeMobileDigits(initialMobile));
  const [notes, setNotes] = useState("");

  const [otpPurpose, setOtpPurpose] = useState(DEFAULT_OTP_PURPOSE);
  const [otpStage, setOtpStage] = useState("idle"); // idle | sent | verified
  const [otpCode, setOtpCode] = useState("");
  const [otpVerificationToken, setOtpVerificationToken] = useState("");
  const [otpVerifiedMobile, setOtpVerifiedMobile] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpDebugCode, setOtpDebugCode] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const registeredDigits = normalizeMobileDigits(registeredMobile || initialMobile);
  const requireOtp =
    requireOtpProp != null ? Boolean(requireOtpProp) && mobile !== registeredDigits : mobile !== registeredDigits || !registeredDigits;

  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    api
      .getEmergencyRequestOptions()
      .then((res) => {
        const purpose = res?.options?.otpPurpose;
        if (purpose) setOtpPurpose(purpose);
      })
      .catch(() => {
        /* fall back to the hardcoded enums/purpose above */
      });
  }, []);

  // Re-verifying is required if the mobile number changes after OTP verification.
  const otpMatchesCurrentMobile = otpStage === "verified" && otpVerifiedMobile === mobile;

  function handleMobileChange(nextValue) {
    setMobile(normalizeMobileDigits(nextValue));
    setOtpError("");
  }

  async function handleSendOtp() {
    if (mobile.length !== 10) {
      setOtpError("Enter a valid 10-digit mobile number first.");
      return;
    }
    setOtpBusy(true);
    setOtpError("");
    setOtpDebugCode("");
    try {
      const res = await api.sendPhoneOtp({ mobile, purpose: otpPurpose });
      if (!mountedRef.current) return;
      setOtpStage("sent");
      if (res?.stubOtp) setOtpDebugCode(String(res.stubOtp));
    } catch (err) {
      if (!mountedRef.current) return;
      setOtpError(err.message || "Could not send OTP. Please try again.");
    } finally {
      if (mountedRef.current) setOtpBusy(false);
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode.trim()) {
      setOtpError("Enter the OTP sent to your mobile.");
      return;
    }
    setOtpBusy(true);
    setOtpError("");
    try {
      const res = await api.verifyPhoneOtp({ mobile, otp: otpCode.trim(), purpose: otpPurpose });
      if (!mountedRef.current) return;
      setOtpVerificationToken(res?.phoneOtpVerificationToken || "");
      setOtpVerifiedMobile(mobile);
      setOtpStage("verified");
    } catch (err) {
      if (!mountedRef.current) return;
      setOtpError(err.message || "Invalid OTP. Please try again.");
    } finally {
      if (mountedRef.current) setOtpBusy(false);
    }
  }

  function validate() {
    if (!serviceType) return "Please choose the service you need.";
    if (!requiredTime) return "Please choose when you need the service.";
    if (!shiftType) return "Please choose the shift required.";
    if (!address.trim()) return "Patient location is required.";
    if (!name.trim()) return "Please enter your name.";
    if (mobile.length !== 10) return "Enter a valid 10-digit mobile number.";
    if (requireOtp && !otpMatchesCurrentMobile) {
      return "Please verify your mobile number with the OTP sent to it.";
    }
    return "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError("");
    setSubmitting(true);
    try {
      const res = await api.createEmergencyRequest({
        name: name.trim(),
        mobile,
        serviceType,
        requiredTime,
        shiftType,
        address: address.trim(),
        lat: coords.lat,
        lng: coords.lng,
        notes: notes.trim(),
        source,
        otpVerificationToken: requireOtp ? otpVerificationToken : undefined,
      });
      if (!mountedRef.current) return;
      setResult(res?.item || null);
      onSuccess?.(res?.item || null);
    } catch (err) {
      if (!mountedRef.current) return;
      const existingNumber = err.existingRequestNumber || err.body?.existingRequestNumber || err.body?.existingItem?.requestNumber;
      if (err.code === "DUPLICATE_EMERGENCY_REQUEST" || err.status === 409) {
        setSubmitError(
          existingNumber
            ? `An emergency request (${existingNumber}) was already submitted for this mobile. Our Care Manager will contact you shortly.`
            : err.message || "An emergency request was already submitted for this mobile number."
        );
      } else {
        setSubmitError(err.message || "We couldn't submit your request. Please try again.");
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  if (result) {
    return (
      <div className="emreq-success" role="status">
        <div className="emreq-success__icon" aria-hidden="true">✓</div>
        <h3>Request Submitted Successfully!</h3>
        <p>
          Thank you for contacting NEMA CareHub. We&apos;ll contact you within 15 minutes to arrange
          the most suitable caregiver.
        </p>
        <dl className="emreq-success__meta">
          <div>
            <dt>Emergency Request ID</dt>
            <dd>{result.requestNumber || "—"}</dd>
          </div>
          <div>
            <dt>Service requested</dt>
            <dd>{SERVICE_LABELS[result.serviceType] || result.serviceType || "—"}</dd>
          </div>
          <div>
            <dt>Submitted</dt>
            <dd>{formatWhen(result.createdAt)}</dd>
          </div>
          <div>
            <dt>Contact number</dt>
            <dd>{result.customerMobile || mobile}</dd>
          </div>
        </dl>
        <p className="emreq-success__note">
          Your request has been received. Caregiver booking is not confirmed yet — a Care Manager
          will assess the requirement and arrange the most suitable caregiver.
        </p>
        {onCancel ? (
          <button type="button" className="emreq-btn emreq-btn--primary" onClick={onCancel}>
            Close
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <form className="emreq-form" onSubmit={handleSubmit}>
      <fieldset className="emreq-field" disabled={submitting}>
        <legend>What service do you need? *</legend>
        <div className="emreq-radio-row">
          {SERVICE_TYPES.map((opt) => (
            <label key={opt.value} className={`emreq-radio${serviceType === opt.value ? " emreq-radio--active" : ""}`}>
              <input
                type="radio"
                name="serviceType"
                value={opt.value}
                checked={serviceType === opt.value}
                onChange={() => setServiceType(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="emreq-field" disabled={submitting}>
        <legend>When do you need the service? *</legend>
        <div className="emreq-radio-row">
          {REQUIRED_TIMES.map((opt) => (
            <label key={opt.value} className={`emreq-radio${requiredTime === opt.value ? " emreq-radio--active" : ""}`}>
              <input
                type="radio"
                name="requiredTime"
                value={opt.value}
                checked={requiredTime === opt.value}
                onChange={() => setRequiredTime(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="emreq-field" disabled={submitting}>
        <legend>Shift Required *</legend>
        <div className="emreq-radio-row">
          {SHIFT_TYPES.map((opt) => (
            <label key={opt.value} className={`emreq-radio${shiftType === opt.value ? " emreq-radio--active" : ""}`}>
              <input
                type="radio"
                name="shiftType"
                value={opt.value}
                checked={shiftType === opt.value}
                onChange={() => setShiftType(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      <EmergencyRequestAddressField
        value={address}
        onChange={setAddress}
        onCoordsChange={(lat, lng) => setCoords({ lat, lng })}
        disabled={submitting}
      />

      <div className="emreq-field">
        <label htmlFor="emreq-name">Your Name *</label>
        <input
          id="emreq-name"
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="Full name"
          disabled={submitting}
          required
        />
      </div>

      <div className="emreq-field">
        <label htmlFor="emreq-mobile">Mobile Number *</label>
        <div className="emreq-mobile-group">
          <div className="emreq-otp-row">
            <input
              id="emreq-mobile"
              type="tel"
              inputMode="numeric"
              value={mobile}
              onChange={(event) => handleMobileChange(event.target.value)}
              placeholder="10-digit mobile number"
              disabled={submitting || (requireOtp && otpStage === "verified")}
              maxLength={10}
              required
            />
            {requireOtp && !otpMatchesCurrentMobile ? (
              <button
                type="button"
                className="emreq-btn emreq-btn--secondary"
                onClick={handleSendOtp}
                disabled={otpBusy || submitting || mobile.length !== 10}
              >
                {otpBusy && otpStage !== "sent" ? "Sending…" : otpStage === "sent" ? "Resend OTP" : "Send OTP"}
              </button>
            ) : null}
          </div>

          {requireOtp && otpStage === "sent" && !otpMatchesCurrentMobile ? (
            <>
              <p className="emreq-field__hint">
                Enter the OTP sent to {mobile} to verify this number.
              </p>
              <div className="emreq-otp-row emreq-otp-row--verify">
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(event) => setOtpCode(event.target.value)}
                  placeholder="Enter OTP"
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="emreq-btn emreq-btn--secondary"
                  onClick={handleVerifyOtp}
                  disabled={otpBusy || submitting}
                >
                  {otpBusy ? "Verifying…" : "Verify"}
                </button>
              </div>
            </>
          ) : null}

          {requireOtp && otpDebugCode ? (
            <p className="emreq-field__hint">Dev mode OTP: {otpDebugCode}</p>
          ) : null}
          {requireOtp && otpMatchesCurrentMobile ? (
            <p className="emreq-field__hint emreq-field__hint--success">✓ Mobile number verified</p>
          ) : null}
          {otpError ? <p className="emreq-field__error">{otpError}</p> : null}
        </div>
      </div>

      <div className="emreq-field">
        <label htmlFor="emreq-notes">Tell us briefly (Optional)</label>
        <textarea
          id="emreq-notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value.slice(0, NOTES_MAX_LENGTH))}
          placeholder="Example: Patient discharged today after hip surgery."
          maxLength={NOTES_MAX_LENGTH}
          rows={3}
          disabled={submitting}
        />
        <span className="emreq-field__counter">{notes.length}/{NOTES_MAX_LENGTH}</span>
      </div>

      {submitError ? <p className="emreq-field__error emreq-field__error--block" role="alert">{submitError}</p> : null}

      <div className="emreq-actions">
        {onCancel ? (
          <button type="button" className="emreq-btn emreq-btn--ghost" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="emreq-btn emreq-btn--primary" disabled={submitting}>
          {submitting ? "Sending…" : "🚨 Request Emergency Care"}
        </button>
      </div>
    </form>
  );
}
